import * as React from "react";
import classNames from "classnames";

import { SEO_URLS } from "./landing_utils";
import { FooterSignUpSection, PromoFeaturesSection } from "./shared";

import css from "./LandingPageLinks.module.scss";

export type Link = {
  text: string;
  path: string;
};

export type QueryTopicLinks = {
  tenders: {
    sources: {
      expiring_contracts: Link[];
      tenders: Link[];
      awarded_contracts: Link[];
    };
    topics: {
      expiring_contracts: Link[];
      tenders: Link[];
      awarded_contracts: Link[];
    };
  };
  buyers: {
    central: Link[];
    local: Link[];
    other: Link[];
  };
};

type Props = {
  links: QueryTopicLinks;
};

const OBJ_TYPE_LABELS = {
  tenders: "open tenders",
  expiring_contracts: "expiring contracts",
  awarded_contracts: "awarded contracts",
};

const DESCRIPTION_BY_KIND = {
  sources:
    "We aggregate all data sources of public sector tenders and contracts into one, easy to understand feed of opportunities. Browse them below:",
  topics:
    "We filter opportunities based on topics made up of keywords and CPV codes the government is procuring for. Browse them below:",
};

export function QueryTopicLinkspam({ links }: Props): JSX.Element {
  // for the time being buyers are static because there are so many more of them, in order to avoid
  // having to make API calls
  const [visibleRowCount, setVisibleRowCount] = React.useState({ sources: 5, topics: 5 });
  const groupedLinks = React.useMemo(() => {
    return (
      <>
        {(["sources", "topics"] as const).map((cat) => (
          <React.Fragment key={cat}>
            <h4>
              Browse by{" "}
              {
                // singularise
                cat.slice(0, -1)
              }
            </h4>
            <p>{DESCRIPTION_BY_KIND[cat]}</p>
            <div className={css.row}>
              <div className={css.categoryWrapper}>
                {Object.entries(links.tenders[cat as keyof (typeof links)["tenders"]])
                  .sort(([objA], [objB]) => objA.localeCompare(objB))
                  .map(([objType, links]) => {
                    return (
                      <div key={objType} className={css.linksCategoryContainer}>
                        <h5>{OBJ_TYPE_LABELS[objType as keyof typeof OBJ_TYPE_LABELS]}</h5>
                        <ul>
                          {links.map((link, i) => (
                            <li
                              key={link.path}
                              style={
                                i < visibleRowCount[cat as keyof typeof visibleRowCount]
                                  ? undefined
                                  : { display: "none" }
                              }
                            >
                              <a href={link.path} data-id="explore_articles">
                                {link.text}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
              </div>
              {Object.values(links.tenders[cat as keyof (typeof links)["tenders"]]).some(
                (linkList) =>
                  linkList.length > visibleRowCount[cat as keyof typeof visibleRowCount],
              ) && (
                <button
                  onClick={() => {
                    setVisibleRowCount((rows) => ({
                      ...rows,
                      [cat]: rows[cat as keyof typeof rows] + 5,
                    }));

                    if (typeof window !== "undefined") {
                      const amplitudeInstance = window.amplitude;
                      if (amplitudeInstance) {
                        amplitudeInstance.track(
                          `"View more links" button clicked (section: ${cat} - ${window.location.pathname})`,
                          {},
                        );
                      }
                    }
                  }}
                  className={css.viewMoreLink}
                >
                  View more
                </button>
              )}
            </div>
          </React.Fragment>
        ))}
        <h4>Browse by buyer profiles</h4>
        <p>
          We highlight data on buying trends and current opportunities for suppliers to get involved
          in with various central, local and third party government organisations. Browse them
          below:
        </p>
        <div className={css.categoryWrapper}>
          {Object.entries(links.buyers)
            .sort(([catA], [catB]) => catA.localeCompare(catB))
            .map(([category, links]) => (
              <div key={category} className={css.linksCategoryContainer}>
                <h5>
                  {
                    // case styled by css
                    category.toLowerCase()
                  }
                </h5>
                <ul>
                  {links.map((link) => (
                    <li key={link.path}>
                      <a href={link.path} data-id="explore_articles">
                        {link.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      </>
    );
  }, [links, visibleRowCount]);

  return (
    // eslint-disable-next-line css-modules/no-undef-class
    <section className={classNames(css.section, css.linksSection)}>
      <h3 className={css.linksHeader}>
        Discover thousands more relevant opportunities with Stotles
      </h3>
      <div className={css.landingPageLinks}>{groupedLinks}</div>
    </section>
  );
}

export function NewLinksSection({
  title,
  description,
  links,
  className,
  footer,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  links: Link[];
  className?: string;
  footer?: React.ReactNode;
}): JSX.Element {
  return (
    <div className={classNames(css.newLinksSection, className)}>
      {title}
      {description}
      <ul>
        {links.map((l) => (
          <li key={l.path}>
            <a href={l.path} title={l.text} data-id="explore_articles">
              {l.text}
            </a>
          </li>
        ))}
      </ul>
      {footer}
    </div>
  );
}

export type TopicLinks = {
  similar_topics: Link[];
  buyers_by_topic: Link[];
  sources_by_topic: Link[];
};

type TopicLinkSpamProps = {
  app_root_url: string;
  subjectName: React.ReactNode;
  descriptor: string;
  topic_linkspam: TopicLinks;
};

/**
 * Has linkspam for topic landing pages which are related to the current topic, buyers who have
 * tenders or contracts that belong to the current topic and sources that publish
 * contracts relating to the current topic
 * @param param0
 * @returns
 */
export function TopicLinkSpam({
  app_root_url,
  subjectName,
  descriptor,
  topic_linkspam,
}: TopicLinkSpamProps): JSX.Element {
  const { similar_topics, buyers_by_topic, sources_by_topic } = topic_linkspam;

  return (
    <>
      {!!similar_topics?.length && (
        <NewLinksSection
          title={
            <h4 className={css.linkspamTitle}>Other tender topics related to {subjectName}</h4>
          }
          description={
            <p className={css.linkSpamDescription}>
              Explore similar open tenders, recent contract awards and upcoming contract expiries,
              based on topics with related CPV codes below.
            </p>
          }
          links={similar_topics}
          footer={
            <div className={classNames(css.exploreMoreContainer, css.lessBottomPadding)}>
              <a
                className={css.exploreMoreText}
                href={SEO_URLS.topicSearch}
                data-id="explore_articles"
              >
                Explore over 2000 topics
              </a>
            </div>
          }
        />
      )}
      <PromoFeaturesSection
        className={similar_topics.length ? css.promoFeaturesSection : undefined}
      />
      <FooterSignUpSection appRootUrl={app_root_url} />
      {!!buyers_by_topic?.length && (
        <NewLinksSection
          title={<h4 className={css.linkspamTitle}>Top buyers relating to {subjectName}</h4>}
          description={
            <p className={css.linkSpamDescription}>
              Discover insights into the most active buyers procuring for {descriptor} services and
              solutions below.
            </p>
          }
          links={buyers_by_topic}
          footer={
            <div className={css.exploreMoreContainer}>
              <a
                className={css.exploreMoreText}
                href={SEO_URLS.buyerSearch}
                data-id="explore_articles"
              >
                Explore over 15,000 buyers
              </a>
            </div>
          }
        />
      )}

      {!!sources_by_topic?.length && (
        <NewLinksSection
          title={
            <h4 className={css.linkspamTitle}>Top sources for {subjectName} contracts & tenders</h4>
          }
          description={
            <p className={css.linkSpamDescription}>
              Stotles aggregates public sector contract data from every major procurement data
              source. Explore opportunities posted by these sources below.
            </p>
          }
          links={sources_by_topic}
          footer={
            <div className={classNames(css.exploreMoreContainer, css.lessBottomPadding)}>
              <a
                className={css.exploreMoreText}
                href={SEO_URLS.sourceSearch}
                data-id="explore_articles"
              >
                Explore more sources
              </a>
            </div>
          }
        />
      )}
    </>
  );
}

export type SourceLinks = {
  other_top_sources: Link[];
  top_buyers_for_source: Link[];
  topics_of_interest: Link[];
};

type SourceLinkSpamProps = {
  app_root_url: string;
  subjectName: React.ReactNode;
  descriptor: string;
  source_linkspam: SourceLinks;
};
/**
 * Has linkspam for topic landing pages which are related to the current topic, buyers who have
 * tenders or contracts that belong to the current topic and sources that publish
 * contracts relating to the current topic
 * @param param0
 * @returns
 */
export function SourceLinkSpam({
  subjectName,
  descriptor,
  app_root_url,
  source_linkspam,
}: SourceLinkSpamProps): JSX.Element {
  const { other_top_sources, top_buyers_for_source, topics_of_interest } = source_linkspam;
  return (
    <>
      {!!other_top_sources?.length && (
        <NewLinksSection
          title={<h4 className={css.linkspamTitle}>Other top sources for contracts & tenders</h4>}
          description={
            <p>
              Explore public sector opportunities from all other major procurement data sources
              below.
            </p>
          }
          links={other_top_sources}
          footer={
            <div className={css.exploreMoreContainer}>
              <a
                className={css.exploreMoreText}
                href={SEO_URLS.sourceSearch}
                data-id="explore_articles"
              >
                Explore more sources
              </a>
            </div>
          }
        />
      )}
      <PromoFeaturesSection
        className={other_top_sources.length ? css.promoFeaturesSection : undefined}
      />
      <FooterSignUpSection appRootUrl={app_root_url} />
      {!!top_buyers_for_source?.length && (
        <NewLinksSection
          title={
            <h4 className={css.linkspamTitle}>Top buyers for {subjectName} contacts & tenders</h4>
          }
          description={
            <p>
              Discover thousands of public sector buyers actively procuring through {descriptor}.
              Explore purchasing trends across central, local and other government organisations.
            </p>
          }
          links={top_buyers_for_source}
          footer={
            <div className={css.exploreMoreContainer}>
              <a
                className={css.exploreMoreText}
                href={SEO_URLS.buyerSearch}
                data-id="explore_articles"
              >
                Explore over 15,000 buyers
              </a>
            </div>
          }
        />
      )}
      {!!topics_of_interest?.length && (
        <NewLinksSection
          title={<h4 className={css.linkspamTitle}>Tender topics you may be interested in</h4>}
          description={
            <p>
              Find more relevant open tenders, recent contract awards and upcoming contract expiries
              across a range of topics created from government procurement CPV codes below.
            </p>
          }
          links={topics_of_interest}
          footer={
            <div className={css.exploreMoreContainer}>
              <a
                className={css.exploreMoreText}
                href={SEO_URLS.topicSearch}
                data-id="explore_articles"
              >
                Explore over 2,000 buyers
              </a>
            </div>
          }
        />
      )}
    </>
  );
}
